import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { CustomValidator, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Alert, Box, Typography } from '@mui/material';

import { useQueuedReport } from '~/hooks/apis/reporting/useQueuedReport';

import { getQueuedReportJobStatus, queueHotelBookingsReport } from '~/services/ReportingService';
import { addMonths, formatDateISO } from '~/services/TimeService';

import QueuedReportControls from './QueuedReportControls';

const validateForm: CustomValidator<HotelBookingsFormData> = (formData, errors) => {
  if (formData && !formData.startAt) {
    errors.startAt.addError('Please enter a start date');
  }
  if (formData && !formData.endAt) {
    errors.endAt.addError('Please enter an end date');
  }
  if (
    formData &&
    formData.purchasedFrom &&
    formData.purchasedBefore &&
    formData.purchasedFrom >= formData.purchasedBefore
  ) {
    errors.purchasedBefore.addError('Purchased before must be after purchased from');
  }
  if (formData && !formData.propertyName && !formData.propertyId && !formData.vendorId && !formData.offerId) {
    errors.propertyName.addError('Enter a hotel name, property ID, vendor ID, or offer ID');
  }

  return errors;
};

const HOW_IT_WORKS = [
  'Returns every LTLE, LPC and Bedbank booking for one hotel, with customer name and email.',
  'Identify the hotel by name (e.g. Sage Hotel Wollongong), Property ID or Vendor ID. Name is a partial match on the property or vendor name, so a short name can match more than one hotel.',
  'Offer ID returns that offer only. Filling in more than one identifier widens the result rather than narrowing it.',
  'Check-in from is inclusive, Check-in before is exclusive. Purchase dates are optional and use the same bounds against order date.',
  'Cancelled bookings and undated Buy Now Book Later bookings are left out unless you tick them on.',
];

const formSchema: RJSFSchema = {
  type: 'object',
  properties: {
    startAt: {
      title: 'Check-in from',
      type: 'string',
      format: 'date',
    },
    endAt: {
      title: 'Check-in before',
      type: 'string',
      format: 'date',
    },
    purchasedFrom: {
      title: 'Purchased from (optional)',
      type: 'string',
      format: 'date',
    },
    purchasedBefore: {
      title: 'Purchased before (optional)',
      type: 'string',
      format: 'date',
    },
    propertyName: {
      title: 'Hotel name',
      type: 'string',
    },
    propertyId: {
      title: 'Property ID',
      type: 'string',
    },
    vendorId: {
      title: 'Vendor ID',
      type: 'string',
    },
    offerId: {
      title: 'Offer ID or LE Offer ID',
      type: 'string',
    },
    includeCancelled: {
      title: 'Include cancelled bookings',
      type: 'boolean',
      default: false,
    },
    includeUndatedBnbl: {
      title: 'Include Buy Now Book Later bookings with no check-in/out dates selected yet',
      type: 'boolean',
      default: false,
    },
  },
  required: ['startAt', 'endAt'],
};

interface HotelBookingsFormData {
  startAt?: string;
  endAt?: string;
  purchasedFrom?: string;
  purchasedBefore?: string;
  propertyName?: string;
  propertyId?: string;
  vendorId?: string;
  offerId?: string;
  includeCancelled?: boolean;
  includeUndatedBnbl?: boolean;
}

const defaultFormData: HotelBookingsFormData = {
  startAt: formatDateISO(),
  endAt: formatDateISO(addMonths(3)),
  includeCancelled: false,
  includeUndatedBnbl: false,
};

const uiSchema = {
  startAt: {
    'ui:widget': 'date',
  },
  endAt: {
    'ui:widget': 'date',
  },
  purchasedFrom: {
    'ui:widget': 'date',
  },
  purchasedBefore: {
    'ui:widget': 'date',
  },
};

// svc-reporting fails the job with this message when the query matches no rows, which is
// what a mistyped identifier looks like. It is a miss, not a fault, so it reads differently.
const NO_BOOKINGS_PREFIX = 'No bookings found';

const IDENTIFIER_LABELS: Array<[keyof HotelBookingsFormData, string]> = [
  ['propertyName', 'Hotel name'],
  ['propertyId', 'Property ID'],
  ['vendorId', 'Vendor ID'],
  ['offerId', 'Offer ID'],
];

function describeIdentifiers(formData: HotelBookingsFormData): string {
  return IDENTIFIER_LABELS.filter(([key]) => formData[key])
    .map(([key, label]) => `${label} "${String(formData[key]).trim()}"`)
    .join(', ');
}

export default function ExportHotelBookingsForm() {
  const [formData, setFormData] = useState<HotelBookingsFormData>(defaultFormData);
  const report = useQueuedReport(
    'hotel-bookings',
    queueHotelBookingsReport,
    getQueuedReportJobStatus,
    'Hotel bookings report failed',
  );
  const isNoBookingsFound = Boolean(report.error?.startsWith(NO_BOOKINGS_PREFIX));
  const searchedFor = describeIdentifiers(formData);

  const handleSubmit = async (event: IChangeEvent<HotelBookingsFormData>) => {
    if (report.isRunning) {
      return;
    }

    const submitted = event.formData ?? formData;
    if (!submitted.startAt || !submitted.endAt) {
      return;
    }

    setFormData(submitted);
    await report.start({
      startAt: submitted.startAt,
      endAt: submitted.endAt,
      purchasedFrom: submitted.purchasedFrom || undefined,
      purchasedBefore: submitted.purchasedBefore || undefined,
      propertyName: submitted.propertyName,
      propertyId: submitted.propertyId,
      vendorId: submitted.vendorId,
      offerId: submitted.offerId,
      includeCancelled: submitted.includeCancelled,
      includeUndatedBnbl: submitted.includeUndatedBnbl,
    });
  };

  return (
    <>
      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 3, color: 'text.secondary' }}>
        {HOW_IT_WORKS.map((point) => (
          <Typography component="li" variant="body2" key={point} sx={{ mb: 0.5 }}>
            {point}
          </Typography>
        ))}
      </Box>

      <Form
        schema={formSchema}
        uiSchema={uiSchema}
        formData={formData}
        onChange={(event) => setFormData(event.formData ?? formData)}
        onSubmit={handleSubmit}
        customValidate={validateForm}
        validator={validator}
        showErrorList={false}
        disabled={report.isRunning}
      >
        <QueuedReportControls
          {...report}
          submitLabel="Email Hotel Bookings Report"
          lookingUpLabel="Looking up bookings…"
          generatingLabel="Generating spreadsheet..."
          errorContent={
            isNoBookingsFound ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <p>{report.error}</p>
                {searchedFor && <p>Searched for {searchedFor}.</p>}
                <p>
                  A mistyped or unknown ID matches nothing rather than erroring, so check the ID against Admin before
                  widening the check-in range.
                </p>
              </Alert>
            ) : undefined
          }
        />
      </Form>
    </>
  );
}
